import React from 'react'

import Link from 'next/link'
import {useRouter} from 'next/router'
import {palette} from 'styled-tools'
import styled from 'styled-components'
import {media} from 'styled-bootstrap-grid'

import {styledTheme} from '@festi/common/themes'
import {onDutyFree} from '@festi/common/constants/channels'
import settings from '@festi/common/constants/settings'

export type StockStatusType =
  | 'in_stock'
  | 'low'
  | 'sold_out'
  | 'showcase'
  | 'no_showcase'

const StatusCircle = styled.div<{color: string}>`
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
`

const Status = styled.span`
  color: ${palette('ui50Solid')};
  font-size: 1rem;
`

const StockStatusWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
`

const Label = styled.div`
  display: flex;
  align-items: center;
  min-width: 84px;
  line-height: 24px;

  ${media.md`
    min-width: 110px;
  `}
`

const Warehouses = styled.div`
  display: flex;
  flex-wrap: wrap;
  line-height: 24px;
  font-size: 1rem;
  font-weight: 500;
`

const Warehouse = styled.div`
  display: flex;
  align-items: center;
  white-space: break-spaces;

  ::after {
    content: ', ';
  }

  &:last-child {
    ::after {
      content: '';
    }
  }
`

const DutyFreeAnchor = styled.a`
  color: inherit;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${palette('lightBlue')};
    outline-offset: 1px;
    border-radius: 4px;
  }
`

const statusMap: Record<string, [string, string]> = {
  in_stock: ['Til á lager', styledTheme.palette.success],
  low: ['Fá eintök', styledTheme.palette.warning],
  sold_out: ['Uppselt', styledTheme.palette.pink],
  showcase: ['Til sýnis', styledTheme.palette.lightBlue],
}

function DutyFreeLink({label}) {
  const {asPath} = useRouter()
  return (
    <Link href={`${settings.dutyfreeUrl}${asPath}`} passHref legacyBehavior>
      <DutyFreeAnchor>{label}</DutyFreeAnchor>
    </Link>
  )
}

interface StockItemProps {
  warehouse: string
  status: StockStatusType
  isInStock: boolean
}

function StockItem({warehouse, status, isInStock}: StockItemProps) {
  const showDutyFreeLink =
    warehouse === 'Flugstöð' &&
    (status === 'in_stock' || status === 'low') &&
    !onDutyFree

  const hideWarehouseVefverslun =
    warehouse === 'Vefverslun' && status === 'sold_out' && isInStock

  if (hideWarehouseVefverslun) return null

  return (
    <Warehouse
      style={{
        color:
          status === 'sold_out'
            ? styledTheme.palette.ui40Solid
            : styledTheme.palette.blue,
      }}
    >
      {showDutyFreeLink ? <DutyFreeLink label={warehouse} /> : warehouse}
    </Warehouse>
  )
}

interface StatusProps {
  status: StockStatusType
  warehouses?: string[]
  isInStock: boolean
}

export default function StockStatus({
  status,
  warehouses,
  isInStock,
}: StatusProps): JSX.Element {
  if (!status || status === 'no_showcase') {
    return null
  }

  const [statusText, color] = statusMap[status]

  return (
    <StockStatusWrapper>
      <Label aria-label={statusText}>
        <StatusCircle color={color} />
        <Status>{statusText}</Status>
      </Label>
      <Warehouses>
        {warehouses?.map((warehouse: string) => (
          <StockItem
            key={warehouse}
            warehouse={warehouse}
            status={status}
            isInStock={isInStock}
          />
        ))}
      </Warehouses>
    </StockStatusWrapper>
  )
}
